export const SUPPORTED_LANGUAGES = [
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', flag: '🇷🇺' },
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '🇬🇧' },
  { code: 'hy', label: 'Armenian', nativeLabel: 'Հայերեն', flag: '🇦🇲' },
  { code: 'uz', label: 'Uzbek', nativeLabel: "O'zbekcha", flag: '🇺🇿' },
  { code: 'kk', label: 'Kazakh', nativeLabel: 'Қазақша', flag: '🇰🇿' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe', flag: '🇹🇷' },
  { code: 'uk', label: 'Ukrainian', nativeLabel: 'Українська', flag: '🇺🇦' },
] as const;

export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/** Safe default when country / Telegram / browser give no signal. */
export const DEFAULT_LANGUAGE: AppLanguage = 'en';
export const LANGUAGE_STORAGE_KEY = 'cozzysim-language';

/**
 * ISO-3166-1 alpha-2 → app language for first-visit geo defaults.
 * Armenians often use Telegram in Russian — country must win over Telegram
 * so AM users get հայերեն, not русский.
 */
export const COUNTRY_LANGUAGE_MAP: Readonly<Record<string, AppLanguage>> = {
  AM: 'hy',
  RU: 'ru',
  BY: 'ru',
  KZ: 'ru',
  UA: 'uk',
  UZ: 'uz',
  TR: 'tr',
};

/** Timezone → country when CF/GeoIP is not available on the client yet. */
const TIMEZONE_COUNTRY: Readonly<Record<string, string>> = {
  'Asia/Yerevan': 'AM',
  'Asia/Tbilisi': 'GE',
  'Europe/Kyiv': 'UA',
  'Europe/Kiev': 'UA',
  'Europe/Moscow': 'RU',
  'Europe/Minsk': 'BY',
  'Asia/Almaty': 'KZ',
  'Asia/Aqtobe': 'KZ',
  'Asia/Aqtau': 'KZ',
  'Asia/Atyrau': 'KZ',
  'Asia/Oral': 'KZ',
  'Asia/Qostanay': 'KZ',
  'Asia/Qyzylorda': 'KZ',
  'Asia/Tashkent': 'UZ',
  'Asia/Samarkand': 'UZ',
  'Europe/Istanbul': 'TR',
};

const LANGUAGE_ALIASES: Record<string, AppLanguage> = {
  ru: 'ru',
  be: 'ru',
  en: 'en',
  hy: 'hy',
  arm: 'hy',
  uz: 'uz',
  kk: 'kk',
  tr: 'tr',
  uk: 'uk',
};

export function isAppLanguage(value: string | null | undefined): value is AppLanguage {
  return SUPPORTED_LANGUAGES.some((lang) => lang.code === value);
}

export function normalizeLanguage(raw?: string | null): AppLanguage | null {
  if (!raw) return null;
  const base = raw.trim().toLowerCase().split(/[-_]/)[0];
  return LANGUAGE_ALIASES[base] || null;
}

export function detectTelegramLanguage(): string | null {
  try {
    const tgLang = window.Telegram?.WebApp?.initDataUnsafe?.user?.language_code;
    return tgLang || null;
  } catch {
    return null;
  }
}

/** Best-effort country hint without network (timezone + navigator region). */
export function detectCountryHint(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && TIMEZONE_COUNTRY[tz]) {
      return TIMEZONE_COUNTRY[tz];
    }
  } catch {
    /* ignore */
  }

  try {
    const nav = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage;
    if (nav) {
      const region = nav.trim().split(/[-_]/)[1]?.toUpperCase();
      if (region && /^[A-Z]{2}$/.test(region)) {
        return region;
      }
    }
  } catch {
    /* ignore */
  }

  return null;
}

export function languageFromCountry(country: string | null | undefined): AppLanguage | null {
  if (!country) return null;
  const mapped = COUNTRY_LANGUAGE_MAP[country.toUpperCase()];
  // Only return languages the miniapp actually ships.
  return mapped && isAppLanguage(mapped) ? mapped : null;
}

/**
 * Detection priority (first match wins):
 * 1. Saved language in localStorage — explicit user choice.
 * 2. Country (timezone / region) — AM→hy even when Telegram UI is Russian.
 * 3. Telegram language_code — useful outside mapped markets.
 * 4. Browser navigator.language.
 * 5. English default.
 */
export function resolveInitialLanguage(): AppLanguage {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (isAppLanguage(stored)) return stored;

  const fromCountry = languageFromCountry(detectCountryHint());
  if (fromCountry) return fromCountry;

  const fromTelegram = normalizeLanguage(detectTelegramLanguage());
  if (fromTelegram) return fromTelegram;

  const fromNavigator = normalizeLanguage(navigator.language);
  if (fromNavigator) return fromNavigator;

  return DEFAULT_LANGUAGE;
}

export function languageLabel(code: AppLanguage): string {
  return SUPPORTED_LANGUAGES.find((lang) => lang.code === code)?.nativeLabel || code;
}

export function getLanguage(code: AppLanguage) {
  return SUPPORTED_LANGUAGES.find((lang) => lang.code === code) || SUPPORTED_LANGUAGES[0];
}
